import { useTranslation } from "react-i18next";
import {
  Flex,
  Grid,
  GridItem, Icon,
  SkeletonText,
  Text,
  useBreakpointValue,
  useToast
} from "@chakra-ui/react";
import { useState } from "react";
import { useGetAllSupplierCategories } from "../../hooks/query";
import { showErrorToast } from "../../utils";
import {
  useGetPaginatedSuppliersWithTiers,
  useSupplierCountWithCompletedQuestionnaire
} from "../../hooks/query/supplier-administration";
import {
  AppliedFilterIcon,
  EmissionsReductionDonutChart,
  SpendCoverage, FilterSupplierByCategory,
  SuppliersToEngage, TopTenLocations
} from "./components";
import * as React from "react";
import { WithAuthorization } from "../../context";
import { Card } from "../../components/Card";
import { Page } from "../../components/Page";
import { FaTruck, FaGlobeAmericas, FaUserCheck } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { routes } from "../../config";

export const supplierAdminDashboardYearsKeys = [2021, 2022, 2023];

function SupplierAdminDashboard() {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierAdminDashboard"
  });
  const toast = useToast();
  const navigate = useNavigate();
  const [categoryFilter, setCategoryFilter] = useState("");

  const {
    data: categories
  } = useGetAllSupplierCategories({
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0
  });

  const {
    data: allSuppliers,
    isFetching: isAllSuppliersFetching
  } = useGetPaginatedSuppliersWithTiers(`page=0&size=1000${categoryFilter && categoryFilter !== "" ?
    `&category=${categories.find(cat => cat.id === categoryFilter)?.name ?? ""}` : ""}`, {
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0
  });

  const {
    data: supplierCountWithCompletedQuestionnaire,
    isFetching: isSupplierCountWithCompletedQuestionnaireFetching
  } = useSupplierCountWithCompletedQuestionnaire(`
    ${categoryFilter && categoryFilter !== "" ? `
      &categoryId=${categories.find(cat => cat.id === categoryFilter)?.id ?? ""}
    ` : ""}`, {
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0
  });

  const donutChartData = window?.location?.hostname?.includes("howdens") ? [
    {
      id: 1,
      value: 3.69,
      actualValue: 13314,
      label: "Emissions reductions to date",
      "color": "hsl(138,78%,41%)"
    },
    {
      id: 2,
      value: 96.31,
      actualValue: 347084,
      label: "Remaining emissions",
      "color": "hsl(220,99%,61%)"
    }
  ] : [];

  const colSpans = useBreakpointValue({
    base: {
      smallCards: 9,
      dataCoverage: 9,
      reductionChart: 9,
      engagementChart: 9,
      topTenLocations: 9
    },
    lg: {
      smallCards: 3,
      dataCoverage: 6,
      reductionChart: 4,
      engagementChart: 5,
      topTenLocations: 6
    },
    xl: {
      smallCards: 3,
      dataCoverage: 6,
      reductionChart: 4,
      engagementChart: 5,
      topTenLocations: 4
    },
    "2xl": {
      smallCards: 2,
      dataCoverage: 4,
      reductionChart: 3,
      engagementChart: 5,
      topTenLocations: 4
    }
  });

  return (
    <Page maxWidth={"100%"} overflow={"auto"}>
      <Grid templateColumns="repeat(9, 1fr)" gap={3} maxWidth={"100%"} overflow={"auto"}>
        <GridItem colSpan={colSpans.smallCards}>
          <Flex flexFlow="column" gap={3}>
            <Card padding={4}>
              <FilterSupplierByCategory categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter} />
            </Card>
            <Card padding={3}>
              <Flex gap={3} alignItems="center">
                <Icon
                  as={FaTruck}
                  inlineSize="14"
                  blockSize="14"
                  color={"black"}
                  backgroundColor={"#cecece"}
                  borderRadius={"50%"}
                  padding={2}
                />
                <Flex flexFlow="column" fontWeight="bold">
                  <Text fontSize="xs" color={"#ababab"}>
                    {t("totalNumberOfSuppliers")}
                  </Text>
                  {isAllSuppliersFetching ?
                    <SkeletonText mt="4" noOfLines={1} spacing="4" skeletonHeight="2" /> :
                    <Flex alignItems={"center"}>
                      <Text fontSize="3xl">
                        {allSuppliers?.totalElements ?? 0}
                      </Text>
                      {categoryFilter !== "" && (
                        <AppliedFilterIcon filterCategory={categoryFilter} categories={categories} />
                      )}
                    </Flex>
                  }
                </Flex>
              </Flex>
            </Card>

            <Card padding={3}>
              <Flex gap={3} alignItems="center">
                <Icon
                  as={FaUserCheck}
                  inlineSize="14"
                  blockSize="14"
                  color={"black"}
                  backgroundColor={"#cecece"}
                  borderRadius={"50%"}
                  padding={2}
                />
                <Flex flexFlow="column" fontWeight="bold">
                  <Text fontSize="xs" color={"#ababab"}>
                    {t('providedAllRequestedData')}
                  </Text>
                  {isSupplierCountWithCompletedQuestionnaireFetching ?
                    <SkeletonText mt="4" noOfLines={1} spacing="4" skeletonHeight="2" /> :
                    <Flex alignItems={"center"}>
                      <Text fontSize="3xl">
                        { supplierCountWithCompletedQuestionnaire ?? 0 }
                      </Text>
                      {categoryFilter !== "" && (
                        <AppliedFilterIcon filterCategory={categoryFilter} categories={categories} />
                      )}
                    </Flex>
                  }
                </Flex>
              </Flex>
            </Card>

            <Card padding={3}>
              <Flex gap={3} alignItems="center">
                <Icon
                  as={FaGlobeAmericas}
                  inlineSize="14"
                  blockSize="14"
                  color={"black"}
                  backgroundColor={"#cecece"}
                  borderRadius={"50%"}
                  padding={2}
                />
                <Flex flexFlow="column" fontWeight="bold">
                  <Text
                    fontSize="xl"
                    textDecoration="underline"
                    cursor="pointer"
                    onClick={() => {
                      navigate(routes.suppliersLocations);
                    }}
                  >
                    {t("checkLocations")}
                  </Text>
                </Flex>
              </Flex>
            </Card>
          </Flex>
        </GridItem>
        <GridItem colSpan={colSpans.dataCoverage}>
          <SpendCoverage filterCategory={categoryFilter} categories={categories} />
        </GridItem>
        <GridItem colSpan={colSpans.reductionChart}>
          <EmissionsReductionDonutChart data={donutChartData} />
        </GridItem>


        <GridItem colSpan={colSpans.engagementChart}>
          <SuppliersToEngage filterCategory={categoryFilter} categories={categories} />
        </GridItem>
        <GridItem colSpan={colSpans.topTenLocations}>
          <TopTenLocations />
        </GridItem>
      </Grid>
    </Page>
  );
}

export default WithAuthorization(SupplierAdminDashboard);